import { useEffect } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { useToast } from "@/shared/hooks/use-toast";
import { bulkNotificationsApi } from "../api/bulkNotifications.api";
import type {
  Campaign,
  Dispatch,
  CreateCampaignPayload,
  UpdateCampaignPayload,
} from "../types";
import { bulkNotificationsKeys } from "./keys";

export function useCampaigns(
  options?: UseQueryOptions<Campaign[], Error>
) {
  const { toast } = useToast();
  const query = useQuery({
    queryKey: bulkNotificationsKeys.campaigns(),
    queryFn: () => bulkNotificationsApi.campaigns.list(),
    staleTime: 2 * 60 * 1000,
    ...options,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao carregar campanhas",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error, toast]);

  return {
    campaigns: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    error: query.error,
    queryResult: query,
  };
}

export function useCampaign(
  id: string | null,
  options?: UseQueryOptions<Campaign | null, Error>
) {
  const { toast } = useToast();
  const query = useQuery({
    queryKey: bulkNotificationsKeys.campaign(id ?? ""),
    queryFn: () =>
      id ? bulkNotificationsApi.campaigns.get(id) : Promise.resolve(null),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    ...options,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao carregar campanha",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error, toast]);

  return {
    campaign: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    error: query.error,
    queryResult: query,
  };
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationKey: [...bulkNotificationsKeys.campaigns(), "create"],
    mutationFn: (payload: CreateCampaignPayload) =>
      bulkNotificationsApi.campaigns.create(payload),
    onSuccess: () => {
      toast({
        title: "Campanha criada",
        description: "A campanha foi criada com sucesso.",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: bulkNotificationsKeys.campaigns() });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao criar campanha",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    createCampaign: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationKey: [...bulkNotificationsKeys.campaigns(), "update"],
    mutationFn: ({
      id,
      payload,
    }: { id: string; payload: UpdateCampaignPayload }) =>
      bulkNotificationsApi.campaigns.update(id, payload),
    onSuccess: (_, { id }) => {
      toast({
        title: "Campanha atualizada",
        description: "As alterações foram salvas.",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: bulkNotificationsKeys.campaigns() });
      queryClient.invalidateQueries({ queryKey: bulkNotificationsKeys.campaign(id) });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao atualizar campanha",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    updateCampaign: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

export function useDispatches(
  campaignId: string | null,
  options?: UseQueryOptions<Dispatch[], Error>
) {
  const { toast } = useToast();
  const query = useQuery({
    queryKey: bulkNotificationsKeys.dispatches(campaignId ?? ""),
    queryFn: () =>
      campaignId
        ? bulkNotificationsApi.dispatches.list(campaignId)
        : Promise.resolve([]),
    enabled: !!campaignId,
    staleTime: 60 * 1000,
    ...options,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao carregar disparos",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error, toast]);

  return {
    dispatches: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    error: query.error,
    queryResult: query,
  };
}
